import {
  capitalize,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  MenuItem,
  Typography,
  ListItem,
  ListItemText,
  Stack,
} from '@mui/material';
import {
  createRule,
  getPropertyGroupRules,
  getPropertyRules,
  getTenantRules,
  getTenantViolations,
  putPropertyGroupRules,
  putPropertyRules,
  updateRule,
} from 'api';
import MobilePanelList from 'components/lists/MobilePanelList';
import { TAB_LAYOUT_HEADER_HEIGHT } from 'components/layout/FormTabNavigation';
import { ACTION_TYPE, AppContext } from 'context/appContext';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionFunction,
  Outlet,
  useFetcher,
  useLoaderData,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router';
import { ROUTES, routeWithParams } from 'router';
import {
  DisplayMode,
  EntityPanelActionHandler,
  EntityTypes,
  FormMode,
  JobType,
  PanelRootContext,
  Property,
  PropertyGroup,
  Rule,
  Status,
  Tenant,
  TraxeroActionResponse,
  Violation,
} from 'types';
import AuthService from 'utils/AuthService';
import { getBlankRule } from 'utils/emptyEntities';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_Row,
} from 'material-react-table';
import { Add, Delete, History, Warning } from '@mui/icons-material';
import EntityLookup from 'components/autocompletes/EntityLookup';
import {
  TableType,
  useDefaultTableOptions,
} from 'hooks/useDefaultTableOptions';
import dayjs from 'dayjs';
import MRTableToolbar from 'components/tables/PanelTableToolbar';
import JobTypeChip from 'components/chips/JobTypeChip';
import { ListItemMenuButton } from 'components/buttons/ListItemMenuButton';
import { useFetcherKey } from 'hooks/useFetcherKey';
import FormFrame from 'components/layout/FormFrame';
export interface RulesLoaderData {
  _rules: Promise<Rule[]>;
  _violations: Promise<Violation[]>;
}

export interface RuleContext {
  rule: Rule;
  setRule: (rule: Rule) => void;
  violations: Violation[];
  rules: Rule[];
  onBack: () => void;
  formMode: FormMode;
  displayMode: DisplayMode;
  fetcherKey: string;
}

export interface RulesRouteParams {
  params: {
    ruleID?: string | undefined;
    propertyID?: string | undefined;
    groupID?: string | undefined;
    tenantID?: string | undefined;
  };
}

export const rulesLoader = ({ params }: RulesRouteParams) => {
  const includeInactive = AuthService.IsSuperAdmin();
  if (params.propertyID) {
    return {
      _rules: getPropertyRules({
        tenantID: params.tenantID,
        propertyID: params.propertyID,
        includeInactive,
      }),
      _violations: getTenantViolations(params.tenantID),
    };
  } else if (params.groupID) {
    return {
      _rules: getPropertyGroupRules({
        tenantID: params.tenantID,
        groupID: params.groupID,
        includeInactive,
      }),
      _violations: getTenantViolations(params.tenantID),
    };
  } else if (params.tenantID) {
    return {
      _rules: getTenantRules(params.tenantID, includeInactive),
      _violations: getTenantViolations(params.tenantID),
    };
  } else {
    return {
      _tenantRules: Promise.resolve([]),
      _violations: Promise.resolve([]),
    };
  }
};

export const RulesPanel = (): ReactNode => {
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const { state, dispatch } = useContext(AppContext);
  const displayToast = useContext(ToastContext);
  const { pathname } = useLocation();
  const { ruleID } = useParams();
  const { fetcherKey } = useFetcherKey();
  const fetcher = useFetcher({ key: fetcherKey });
  const navigate = useNavigate();
  const mobile = useMediaQuery(breakpoints.down('lg'));

  const { formMode, setFormMode, entity, displayMode } =
    useOutletContext<PanelRootContext<Property | PropertyGroup | Tenant>>();
  const { _rules, _violations } = useLoaderData() as RulesLoaderData;
  const [rule, setRule] = useState<Rule>(getBlankRule(state.tenant.id));
  const [violations, setViolations] = useState<Violation[]>([]);
  const [rules, setRules] = useState<Rule[]>(entity?.rules || []);
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_HEADER_TEXT,
      payload: t('rule.label_plural', { ns: 'entities' }),
    });
  }, [entity]);

  const canAddRemove = useMemo(() => {
    if (AuthService.IsSuperAdmin() && displayMode !== 'tenant') {
      return true;
    }
    if (displayMode === 'property') {
      return state.entityPermissions[EntityTypes.Property].update;
    }
    if (displayMode === 'group') {
      return state.entityPermissions[EntityTypes.PropertyGroup].update;
    }
    return false;
  }, [displayMode]);

  // await data fetch
  useEffect(() => {
    const awaitLoaderData = async () => {
      const rules = await _rules;
      setRules(rules);
      if (ruleID && !rule.id) {
        const rule = rules.find((r) => r.id === Number(ruleID));
        if (rule) {
          setRule(rule);
        }
      }
      const violations = await _violations;
      setViolations(violations);
      setIsLoading(false);
    };
    awaitLoaderData();
  }, [_rules, _violations]);

  // set blank rule on create
  useEffect(() => {
    if (formMode === 'create') {
      setRule(getBlankRule(state.tenant.id));
    }
  }, [formMode]);

  // handle action responses
  const getSuccessMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      switch (res.handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_success', {
            name: `${t('rule.label', { ns: 'entities' })} ${rule.name}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_CREATE:
        case EntityPanelActionHandler.GROUP_CREATE:
        case EntityPanelActionHandler.TENANT_CREATE:
          return t('create_success', {
            name: `${t('rule.label', { ns: 'entities' })} ${rule.name}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_UPSERT:
          return t('update_success', {
            name: `${t('property.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.GROUP_UPSERT:
          return t('update_success', {
            name: `${t('property_group.label', { ns: 'entities' })}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_success', {
            name: `${t('rule.label', { ns: 'entities' })} ${
              res.body?.name || ''
            }`,
            ns: 'toasts',
          });
        default:
          return '';
      }
    },
    [],
  );
  const getErrorMessage = useCallback(
    (res: TraxeroActionResponse<EntityPanelActionHandler>): string => {
      const { handler, status, statusText } = res;
      switch (handler) {
        case EntityPanelActionHandler.UPDATE:
          return t('update_error', {
            name: `${t('rule.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_CREATE:
        case EntityPanelActionHandler.GROUP_CREATE:
        case EntityPanelActionHandler.TENANT_CREATE:
          return t('create_error', {
            name: `${t('rule.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.PROPERTY_UPSERT:
          return t('update_error', {
            name: `${t('property.label', { ns: 'entities' })}${t(
              'rule.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.GROUP_UPSERT:
          return t('update_error', {
            name: `${t('property_group.label', { ns: 'entities' })}${t(
              'rule.label_plural',
              { ns: 'entities' },
            )}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        case EntityPanelActionHandler.DELETE:
          return t('delete_error', {
            name: `${t('rule.label', { ns: 'entities' })}`,
            error: `${status} - ${statusText}`,
            ns: 'toasts',
          });
        default:
          return t('unknown_error', { ns: 'toasts' });
      }
    },
    [],
  );
  const response =
    fetcher.data as TraxeroActionResponse<EntityPanelActionHandler> | null;
  useEffect(() => {
    if (response) {
      if (response.status < 400) {
        displayToast({
          type: TOAST_TYPE.SUCCESS,
          message: getSuccessMessage(response),
        });
        if (response.handler !== EntityPanelActionHandler.DELETE) {
          handleCancelAction();
        }
      } else {
        displayToast({
          type: TOAST_TYPE.ERROR,
          message: getErrorMessage(response),
        });
      }
    }
  }, [fetcher]);

  const handleRemoveRule = (id: number) => {
    const newRules: Rule[] = rules.filter((r) => r.id !== id);
    fetcher.submit(
      {
        body: JSON.stringify(newRules),
        handler: getHandler({ displayMode, formMode }),
      },
      {
        method: 'PUT',
        action: pathname,
        encType: 'application/json',
      },
    );
  };
  const handleCancelAction = useCallback(() => {
    setFormMode('none');
    setRule(getBlankRule(state.tenant.id));
    // resolve the list route explicitly -- reading the current pathname here
    // would point at the form route the user is trying to leave
    navigate(
      routeWithParams(getActionRouteForDisplayMode(displayMode), [
        state.tenant.id,
        entity?.id,
      ]),
      { replace: true },
    );
  }, [displayMode, state.tenant.id, entity?.id, navigate]);
  const handleViewRevisions = useCallback(
    (id: number) => () => {
      navigate(
        routeWithParams(ROUTES.REVISIONS, [
          state.tenant.id,
          EntityTypes.Rule,
          id.toString(),
        ]),
      );
    },
    [],
  );
  const handleRuleClick = (id: number) => {
    const selected: Rule | undefined = rules.find((r) => r.id === id);
    if (selected) {
      setRule(selected);
      setFormMode('edit');
      if (displayMode === 'tenant') {
        navigate(routeWithParams(ROUTES.RULES_FORM, [state.tenant.id, id]));
        return;
      } else {
        navigate(
          routeWithParams(getFormRouteForDisplayMode(displayMode), [
            state.tenant.id,
            entity?.id,
            id,
          ]),
        );
      }
    } else {
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('find_error', {
          name: t('rule.label', { ns: 'entities' }),
          error: t('errors.notFound'),
          ns: 'toasts',
        }),
      });
    }
  };
  const handleAddRule = (r: Rule | null) => {
    if (r) {
      const updatedRules = [...rules, r];
      fetcher.submit(
        {
          handler: getHandler({ displayMode, formMode }),
          body: JSON.stringify(updatedRules),
        },
        {
          method: 'PUT',
          action: pathname,
          encType: 'application/json',
        },
      );
    }
  };
  const handleCreateClick = () => {
    setFormMode('create');
    setRule(getBlankRule(state.tenant.id));
    navigate(`${pathname}/0`);
  };

  const handleSoftDelete = (rule: Rule): void => {
    const updatedRule = { ...rule, status: Status.Disabled };
    fetcher.submit(
      {
        handler: EntityPanelActionHandler.DELETE,
        body: JSON.stringify(updatedRule),
      },
      {
        method: 'PUT',
        action: pathname,
        encType: 'application/json',
      },
    );
  };

  const columns: MRT_ColumnDef<Rule>[] = useMemo(() => {
    const _columns: MRT_ColumnDef<Rule>[] = [
      {
        header: t('rule.id', { ns: 'entities' }),
        accessorKey: 'id',
      },
      {
        header: t('rule.name', { ns: 'entities' }),
        accessorKey: 'name',
      },
      {
        header: t('rule.description', { ns: 'entities' }),
        accessorKey: 'description',
      },
      {
        header: t('rule.startDate', { ns: 'entities' }),
        id: 'startDate',
        accessorFn: (row) => new Date(row.startDate),
        Cell: ({ cell }) => dayjs(cell.getValue<Date>()).format('MM/DD/YYYY'),
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
      {
        header: t('rule.endDate', { ns: 'entities' }),
        id: 'endDate',
        accessorFn: (row) => (row.endDate ? new Date(row.endDate) : null),
        Cell: ({ cell }) =>
          cell.getValue<Date>()
            ? dayjs(cell.getValue<Date>()).format('MM/DD/YYYY')
            : '',
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
      {
        header: t('rule.data.ruleType', { ns: 'entities' }),
        accessorKey: 'data.ruleType',
        Cell: ({ row }: { row: MRT_Row<Rule> }) => (
          <JobTypeChip type={row.original.data.ruleType} size={'small'} />
        ),
        filterVariant: 'select',
        filterSelectOptions: [
          JobType.TOW,
          JobType.TAG,
          JobType.BOOT,
          JobType.PATROL,
        ],
        // muiFilterTextFieldProps: {
        //   fullWidth: false,
        //   SelectProps: {
        //     renderValue: (value: unknown) => {
        //       const arr = value as JobType[];
        //       return arr.map((type) => (
        //         <JobTypeChip type={type} key={type} size='small' />
        //       ));
        //     },
        //   },
        // },
      },
      {
        header: t('rule.status', { ns: 'entities' }),
        accessorKey: 'status',
        filterVariant: 'select',
        filterSelectOptions: [
          {
            label: t('status.active', { ns: 'enums' }),
            value: Status.Active,
          },
          {
            label: t('status.disabled', { ns: 'enums' }),
            value: Status.Disabled,
          },
        ],
        id: 'status',
        Cell: ({ row }: { row: MRT_Row<Rule> }) => {
          return capitalize(row.original.status);
        },
      },
      {
        header: t('rule.created', { ns: 'entities' }),
        id: 'created',
        accessorFn: (row) => (row.created ? new Date(row.created) : null),
        Cell: ({ cell }) =>
          cell.getValue<Date>()
            ? dayjs(cell.getValue<Date>()).format('MM/DD/YYYY')
            : '',
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
      {
        header: t('rule.updated', { ns: 'entities' }),
        id: 'updated',
        accessorFn: (row) => (row.updated ? new Date(row.updated) : null),
        Cell: ({ cell }) =>
          cell.getValue<Date>()
            ? dayjs(cell.getValue<Date>()).format('MM/DD/YYYY')
            : '',
        filterVariant: 'date',
        filterFn: (row, id, filterValue) => {
          return dayjs(row.getValue(id)).isSame(dayjs(filterValue), 'day');
        },
      },
    ];
    if (displayMode !== 'tenant') {
      _columns.push({
        header: t('from', { name: t('ruleset.label', { ns: 'entities' }) }),
        accessorKey: 'ancestry.rulesetName',
        id: 'rulesetName',
      });
    }
    if (displayMode === 'property') {
      _columns.push({
        header: t('from', {
          name: t('property_group.label', { ns: 'entities' }),
        }),
        accessorKey: 'ancestry.propertyGroupName',
        id: 'propertyGroupName',
      });
    }

    return _columns;
  }, [displayMode]);

  // desktop table
  const tableDefaults = useDefaultTableOptions<Rule>(TableType.NESTED);
  const table = useMaterialReactTable({
    ...tableDefaults,
    data: rules,
    columns,
    initialState: {
      showColumnFilters: false,
      showGlobalFilter: true,
      density: 'compact',
      columnVisibility: {
        id: false,
        name: true,
        description: true,
        startDate: false,
        endDate: false,
        ruleType: true,
        status: true,
        created: false,
        updated: false,
        rulesetName: false,
        propertyGroupName: false,
      },
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    state: {
      isLoading,
      sorting,
      showLoadingOverlay: false,
    },
    onSortingChange: setSorting,
    getRowId: (row) => `${row.id}`,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: 'white',
        cursor: 'pointer',
      },
      onClick: () => handleRuleClick(row.original.id),
    }),
    renderTopToolbar: ({ table }) => (
      <MRTableToolbar
        table={table}
        optionsDisabled={!state.entityPermissions[EntityTypes.Rule].create}
        isSubmitting={fetcher.state === 'submitting'}
        tableType={TableType.NESTED}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('rule.label', { ns: 'entities' }),
            }),
            disabled: !state.entityPermissions[EntityTypes.Rule].create,
            icon: <Add />,
          },
        ]}
      >
        <Stack
          direction={'row'}
          flexGrow={1}
          justifyContent={
            displayMode === 'tenant' ? 'space-between' : 'flex-start'
          }
          alignItems={'center'}
        >
          <Typography
            variant='h3'
            textTransform={'capitalize'}
            sx={{ flex: 1 }}
          >
            {t('rule.label_plural', { ns: 'entities' })}
          </Typography>

          {displayMode !== 'tenant' && (
            <EntityLookup
              entityType={EntityTypes.Rule}
              onSelect={handleAddRule}
              renderOption={(option: Rule) => {
                return (
                  <React.Fragment>
                    <Typography variant={'body2'} noWrap fontWeight={'bold'}>
                      {`${option.name}`}
                    </Typography>
                    <Typography variant={'body2'} noWrap>
                      {`${option.description}`}
                    </Typography>
                  </React.Fragment>
                );
              }}
              size='small'
              sx={{ width: '30%' }}
            />
          )}
        </Stack>
      </MRTableToolbar>
    ),
    renderRowActionMenuItems: ({ closeMenu, row }) => {
      const options = [
        <MenuItem
          key={1}
          divider
          onClick={() => {
            handleRemoveRule(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
          disabled={!canAddRemove}
        >
          <ListItemIcon>
            <Delete />
          </ListItemIcon>
          {t('buttons.remove', {
            name: t('rule.label', { ns: 'entities' }),
          })}
        </MenuItem>,
        <MenuItem
          key={2}
          divider
          onClick={() => {
            handleViewRevisions(row.original.id);
            closeMenu();
          }}
          sx={{ m: 0, backgroundColor: 'white' }}
        >
          <ListItemIcon>
            <History />
          </ListItemIcon>
          {t('buttons.view', { name: t('revision.label_plural') })}
        </MenuItem>,
      ];
      if (AuthService.IsSuperAdmin()) {
        options.push(
          <MenuItem
            key={3}
            onClick={() => {
              handleSoftDelete(row.original);
              closeMenu();
            }}
            sx={{
              m: 0,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            <ListItemIcon>
              <Warning sx={{ color: 'white' }} />
            </ListItemIcon>
            {t('buttons.delete', {
              name: t('rule.label', { ns: 'entities' }),
            })}
          </MenuItem>,
        );
      }
      return options;
    },
  });

  // form outlet
  if (formMode !== 'none') {
    return (
      <Outlet
        context={
          {
            rule,
            setRule,
            rules,
            violations,
            onBack: handleCancelAction,
            formMode,
            displayMode,
            fetcherKey,
          } satisfies RuleContext
        }
      />
    );
  }

  // mobile list
  if (mobile) {
    const itemHeight = 70;
    const renderItem = (item: Rule): ReactNode => (
      <ListItem
        key={item.id}
        id={item.id.toString()}
        onClick={() => handleRuleClick(item.id)}
        divider
        secondaryAction={
          <ListItemMenuButton
            menuOptions={[
              {
                content: t('buttons.remove', {
                  name: t('rule.label', { ns: 'entities' }),
                }),
                onClick: () => handleRemoveRule(item.id),
                disabled: !canAddRemove || !item.ancestry,
              },
            ]}
          />
        }
        sx={{ height: itemHeight, backgroundColor: 'white' }}
      >
        <ListItemText
          primary={item.name}
          secondary={item.description}
          primaryTypographyProps={{
            noWrap: true,
            variant: 'subtitle1',
          }}
          secondaryTypographyProps={{
            noWrap: true,
            variant: 'subtitle2',
          }}
        />
      </ListItem>
    );

    return (
      <MobilePanelList<Rule>
        data={rules}
        columns={columns.map((c) => ({
          name: c.header,
          searchKey: c.accessorKey,
        }))}
        renderItem={renderItem}
        itemHeight={itemHeight}
        title={t('rule.label_plural', { ns: 'entities' })}
        menuOptions={[
          {
            onClick: () => handleCreateClick(),
            content: t('buttons.create', {
              name: t('rule.label', { ns: 'entities' }),
            }),
            disabled: !state.entityPermissions[EntityTypes.Rule].create,
            icon: <Add />,
          },
        ]}
        withSearchBar
        heightOffset={TAB_LAYOUT_HEADER_HEIGHT}
      />
    );
  }

  return (
    <FormFrame loading={false}>
      <MaterialReactTable table={table} />
    </FormFrame>
  );
};

export const getActionRouteForDisplayMode = (
  displayMode: DisplayMode,
): ROUTES => {
  switch (displayMode) {
    case 'property':
      return ROUTES.PROPERTY_RULES;
    case 'group':
      return ROUTES.PROPERTY_GROUP_RULES;
    case 'tenant':
      return ROUTES.RULES;
    default:
      return ROUTES.PROPERTY_RULES;
  }
};
export const getFormRouteForDisplayMode = (
  displayMode: DisplayMode,
): ROUTES => {
  switch (displayMode) {
    case 'property':
      return ROUTES.PROPERTY_RULES_FORM;
    case 'group':
      return ROUTES.PROPERTY_GROUP_RULES_FORM;
    case 'tenant':
      return ROUTES.RULES_FORM;
    default:
      return ROUTES.INDEX;
  }
};
interface GetHandlerParams {
  displayMode: DisplayMode;
  formMode: FormMode;
}
export const getHandler = ({
  displayMode,
  formMode,
}: GetHandlerParams): EntityPanelActionHandler => {
  switch (displayMode) {
    case 'property':
      return formMode === 'create'
        ? EntityPanelActionHandler.PROPERTY_CREATE
        : EntityPanelActionHandler.PROPERTY_UPSERT;
    case 'group':
      return formMode === 'create'
        ? EntityPanelActionHandler.GROUP_CREATE
        : EntityPanelActionHandler.GROUP_UPSERT;
    case 'tenant':
      return EntityPanelActionHandler.TENANT_CREATE;
    default:
      return EntityPanelActionHandler.TENANT_CREATE;
  }
};

export const rulesAction: ActionFunction = async ({
  params,
  request,
}): Promise<TraxeroActionResponse<EntityPanelActionHandler>> => {
  const { body, handler } = await request.json();
  const { tenantID, propertyID, groupID, ruleID } =
    params as RulesRouteParams['params'];
  let res: Response;
  let ruleData: Rule | null = null;
  try {
    switch (handler) {
      case EntityPanelActionHandler.UPDATE:
        res = await updateRule({
          tenantID,
          ruleID,
          body,
        });
        break;
      case EntityPanelActionHandler.TENANT_CREATE:
        res = await createRule({
          tenantID,
          body,
        });
        break;
      case EntityPanelActionHandler.DELETE: {
        const rule = JSON.parse(body) as Rule;
        ruleData = rule;
        res = await updateRule({
          tenantID,
          ruleID: rule.id,
          body,
        });
        break;
      }
      case EntityPanelActionHandler.PROPERTY_CREATE:
      case EntityPanelActionHandler.PROPERTY_UPSERT:
        res = await putPropertyRules({
          tenantID,
          propertyID,
          body,
        });
        break;
      case EntityPanelActionHandler.GROUP_CREATE:
      case EntityPanelActionHandler.GROUP_UPSERT:
        res = await putPropertyGroupRules({
          tenantID,
          groupID,
          body,
        });
        break;
      default: {
        res = new Response(null, {
          status: 404,
          statusText: 'Unknown Handler',
        });
      }
    }
  } catch (e) {
    res = new Response(null, { status: 500, statusText: 'Network Error' });
  }
  return {
    handler,
    status: res.status,
    statusText: res.statusText,
    body: ruleData,
  };
};

export * from './RuleForm';
